import { Head, router } from "@inertiajs/react"
import { Button, Card, Code, Flex, Heading, Table, Text } from "@radix-ui/themes"
import { z } from "zod"

import ProjectLayout from "@/layouts/project-layout"
import { projectDashboardPath, testProjectCampaignPath, projectCampaignPath } from "@/routes"
import { ProjectSchema } from "@/schemas/project"
import { CampaignSchema, type Campaign } from "@/schemas/campaign"
import { CampaignExecutionSchema } from "@/schemas/campaign-execution"
import RecentCampaignExecutions from "@/components/campaigns/recent-campaign-executions"

const PropsSchema = z.object({
  project: ProjectSchema,
  campaigns: z.array(CampaignSchema),
  recent_campaign_executions: z.array(CampaignExecutionSchema),
})

type Props = z.infer<typeof PropsSchema>

export default function CampaignsIndex(props: Props) {
  const validatedProps = PropsSchema.parse(props)
  const { campaigns, project, recent_campaign_executions } = validatedProps

  const handleTestCampaign = (campaignId: string) => {
    const email = prompt("Enter customer email to test the campaign:")

    if (!email) {
      return
    }

    router.post(testProjectCampaignPath(project.slug, campaignId), {
      email: email,
      campaign_id: campaignId,
    })
  }

  const formatSteps = (steps: Campaign["steps"]) => {
    return steps.map((step, index) => {
      if (step.type === "delay") {
        return (
          <div key={index}>
            {index + 1}. Wait {step.duration} {step.unit}
          </div>
        )
      } else if (step.type === "email") {
        return (
          <div key={index}>
            {index + 1}. Send email using template <Code>{step.template}</Code>
          </div>
        )
      } else if (step.type === "push") {
        return (
          <div key={index}>
            {index + 1}. Send push notification <Code>{step.title}</Code>
          </div>
        )
      } else if (step.type === "condition") {
        return (
          <div key={index}>
            {index + 1}. Check if user performed <Code>{step.performed_event}</Code>
            {step.should_exit ? " (exit if true)" : " (continue if true)"}
          </div>
        )
      }
      return null
    })
  }

  return (
    <ProjectLayout
      projectId={project.slug}
      breadcrumbs={[
        { title: project.name, href: projectDashboardPath(project.slug) },
        { title: "Campaigns" },
      ]}
    >
      <Head title="Campaigns" />

      <Flex direction="column" gap="6" p="6">
        <Flex justify="between" align="center">
          <Heading size="6" weight="medium">
            Campaigns
          </Heading>
        </Flex>

        <RecentCampaignExecutions executions={recent_campaign_executions} project={project} />

        <Flex justify="between" align="center">
          <Heading size="4" weight="medium">
            Campaigns
          </Heading>
        </Flex>

        <Text>
          Campaigns can be added by editing the <Code>campaigns.yml</Code> file in the project root.
        </Text>

        <Card>
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Description</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Trigger</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Steps</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Actions</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {campaigns.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={5} style={{ textAlign: "center", color: "var(--gray-11)" }}>
                    No campaigns found
                  </Table.Cell>
                </Table.Row>
              ) : (
                campaigns.map((campaign) => (
                  <Table.Row key={campaign.id}>
                    <Table.Cell>
                      <Text>
                        <a
                          href={projectCampaignPath(project.slug, campaign.id)}
                          style={{ textDecoration: "none", color: "var(--accent-11)" }}
                        >
                          {campaign.name}
                        </a>
                      </Text>
                    </Table.Cell>
                    <Table.Cell>{campaign.description}</Table.Cell>
                    <Table.Cell>
                      {campaign.trigger.type === "segment_enter" ? "Enter" : "Exit"}{" "}
                      <Code>{campaign.trigger.segment}</Code>
                    </Table.Cell>
                    <Table.Cell>{formatSteps(campaign.steps)}</Table.Cell>
                    <Table.Cell>
                      <Button
                        size="1"
                        variant="outline"
                        onClick={() => handleTestCampaign(campaign.id)}
                      >
                        Test
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table.Root>
        </Card>
      </Flex>
    </ProjectLayout>
  )
}
